/**
 * CRDT schema v2 vocabulary — root keys and marker kinds.
 *
 * Single source of truth mirroring the authoritative CRDT schema v2 wire contract shared with the
 * backend. Consumers must import these constants rather than re-declaring the string literals.
 */

/**
 * Top-level keys of the v2 `Y.Doc`.
 *
 * - `text` (`"t"`) — the root `XmlText` stream (words + inline markers).
 * - `timings` — `Map<utteranceId, Array<[start, end]>>` sidecar.
 * - `speakers` — `Map<speakerId, { name, roleId? }>`.
 * - `clients` — audit author registry (`str(clientId) -> { name, type }`); BE-owned.
 * - `recordingStartedAt` — single-element `Array`; `[0]` is the unix-seconds wall-clock
 *   anchor for absolute paragraph times (empty/missing → 0).
 * - `qac` — BE-derived active Q/A holders (`{ activeQ, activeA }`); FE reads only.
 * - `version` — schema version marker written once at document creation.
 */
export const ROOT_KEYS = {
    text: 't',
    timings: 'timings',
    speakers: 'speakers',
    clients: 'clients',
    recordingStartedAt: 'recordingStartedAt',
    qac: 'qac',
    version: 'version',
} as const

/**
 * Inline marker `kind` values embedded in the `doc["t"]` stream.
 *
 * - `paragraphChange` — paragraph boundary carrying mutable `speakerId`/`qac` (a live `Map`).
 * - `utteranceChange` — ASR utterance boundary carrying `id` (join key into `timings`) + `final`.
 * - `auditEvent` — audit annotation (placeholder this phase; emission deferred).
 */
export const MARKER_KINDS = {
    paragraphChange: 'paragraph_change',
    utteranceChange: 'utterance_change',
    auditEvent: 'audit_event',
} as const

export type RootKey = (typeof ROOT_KEYS)[keyof typeof ROOT_KEYS]

export type MarkerKind = (typeof MARKER_KINDS)[keyof typeof MARKER_KINDS]
